import Bottombar from "@/components/bottombar"
import Button from "@/components/button"
import Combobox, { ComboboxOption } from "@/components/ui/combobox"
import {
  Sheet as ShadSheet,
  SheetContent as ShadSheetContent,
  SheetHeader as ShadSheetHeader,
  SheetTitle as ShadSheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"
import { UseFormReturn } from "@/lib/form/form"
import { ProductSchema } from "@/lib/form/schema"
import { FormParamStatus } from "@/lib/form/types"
import { filterProductsWithoutExistingLines } from "@/lib/offline-dedupe"
import { schemaToFormData } from "@/ipqc/lib/utils"
import {
  addProductWithSchema,
  createOfflineSessionSnapshot,
  ensureOfflineBundleAvailable,
  getIpqcOfflineBundleErrorMessage,
  hasDeferredSyncLines,
} from "@/services/ipqc"
import { isNetworkUnavailableError } from "@/services/base"
import { saveOfflineIpqcSession } from "@/ipqc/lib/offline-store"
import useStore from "@/store"
import { ShiftDialog, toLineStatusKey } from "@/store/ipqc"
import { difference } from "lodash"
import { useEffect, useState } from "react"
import { toast } from "sonner"

export interface AddProductsDialogProps {
  form: UseFormReturn
}

export default function AddProductsDialog() {
  const ipqcStore = useStore.use.ipqc()
  const sessionStore = useStore.use.session()
  const schema = ipqcStore.schemas.self
  const products = ipqcStore.shift.products
  const currDialog = ipqcStore.shift.currDialog
  const setShiftDialog = ipqcStore.setShiftDialog
  const options = useProductOptions()
  const [selected, setSelected] = useState<string[]>(
    schema?.products.map((item) => {
      return item.id
    }) || []
  )
  const [defaultSelected, setDefaultSelected] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useInit()

  function useInit() {
    useEffect(() => {
      const newSelected: string[] = []
      schema?.products.forEach((item) => {
        newSelected.push(item.id)
      })
      setDefaultSelected(newSelected)
      setSelected(newSelected)
    }, [schema])
  }

  function useProductOptions() {
    const [options, setOptions] = useState<ComboboxOption[]>([])

    useEffect(() => {
      if (!products) {
        return
      }

      setOptions(
        products.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      )
    }, [products])

    return options
  }

  function handleOpenChange(open: boolean) {
    if (!open) {
      setShiftDialog(ShiftDialog.None)
    }
  }

  function handleSelectProducts(value: string[]) {
    setSelected(value as string[])
  }

  function isRecoverableNetworkAddError(error: unknown): boolean {
    return (
      isNetworkUnavailableError(error) ||
      (error instanceof Error && error.name === "AbortError")
    )
  }

  async function getOfflineProducts(productIds: string[]): Promise<ProductSchema[]> {
    const bundle = await ensureOfflineBundleAvailable({ isOnline: false })

    return bundle.products
      .filter((product) => productIds.includes(product.id))
      .map((product) => JSON.parse(JSON.stringify(product)) as ProductSchema)
  }

  async function appendProductsToStore(productsToAppend: ProductSchema[]) {
    const existingProducts = useStore.getState().ipqc.schemas.self.products
    const uniqueProductsToAppend = filterProductsWithoutExistingLines({
      existingProducts,
      incomingProducts: productsToAppend,
    })

    if (!uniqueProductsToAppend.length) {
      return
    }

    ipqcStore.addSelfProductSchemas(uniqueProductsToAppend)

    const lineStatuses: Record<string, FormParamStatus | undefined> = {}

    uniqueProductsToAppend.forEach((prod) => {
      prod.productionLines.forEach((line) => {
        lineStatuses[toLineStatusKey({ productId: prod.id, lineId: line.id })] =
          line.status || FormParamStatus.PENDING
      })
    })

    const allLineStatuses = { ...ipqcStore.lineStatus, ...lineStatuses }
    ipqcStore.setLineStatuses(allLineStatuses)

    const newProductsFormData = schemaToFormData(
      { products: uniqueProductsToAppend },
      allLineStatuses,
      "self"
    )
    const existingFormData = ipqcStore.forms.self
    ipqcStore.setSelfFormData({
      ...existingFormData,
      products: [...existingFormData.products, ...newProductsFormData.products],
    })

    if (ipqcStore.offline.activeSession) {
      const nextSchema = {
        products: [...existingProducts, ...uniqueProductsToAppend],
      }
      ipqcStore.updateOfflineSessionMeta({
        isDirty: true,
        hasDeferredSync:
          ipqcStore.offline.activeSession.hasDeferredSync ||
          hasDeferredSyncLines(nextSchema),
      })
      await saveOfflineIpqcSession(createOfflineSessionSnapshot())
    }
  }

  async function handleAddProducts() {
    const newProducts = difference(selected, defaultSelected)
    if (!newProducts.length) {
      setShiftDialog(ShiftDialog.None)
      return
    }

    setIsLoading(true)

    try {
      const isOnline =
        typeof navigator === "undefined" ? true : navigator.onLine
      const allNewProducts: ProductSchema[] = []

      if (isOnline) {
        const shiftStartTime = sessionStore.shift.startedAt || undefined
        const shiftEndTime = sessionStore.shift.endedAt || undefined
        const serverSessionId = ipqcStore.offline.activeSession?.serverSessionId

        for (const [index, productId] of newProducts.entries()) {
          try {
            const addedProducts = await addProductWithSchema(
              productId,
              serverSessionId || "",
              shiftStartTime,
              shiftEndTime
            )
            allNewProducts.push(...addedProducts)
          } catch (err) {
            if (!isRecoverableNetworkAddError(err)) {
              throw err
            }

            const remainingProductIds = newProducts.slice(index)
            const offlineProducts = await getOfflineProducts(remainingProductIds)
            allNewProducts.push(...offlineProducts)
            break
          }
        }
      } else {
        allNewProducts.push(...(await getOfflineProducts(newProducts)))
      }

      await appendProductsToStore(allNewProducts)

      const updatedSelected = [...defaultSelected, ...newProducts]
      setDefaultSelected(updatedSelected)
      setSelected(updatedSelected)
      setShiftDialog(ShiftDialog.None)
    } catch (err) {
      console.error("Failed to add products:", err)
      toast.error(getIpqcOfflineBundleErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  function close() {
    setShiftDialog(ShiftDialog.None)
  }

  return (
    <ShadSheet
      open={currDialog === ShiftDialog.AddProducts}
      onOpenChange={handleOpenChange}
    >
      <ShadSheetContent
        side="bottom"
        className="shadow-drop-shadow mx-auto max-w-md rounded-t-lg"
      >
        <ShadSheetHeader>
          <ShadSheetTitle className="text-xl">Add Products</ShadSheetTitle>
        </ShadSheetHeader>

        <SheetDescription className="sr-only">
          Select products to add
        </SheetDescription>

        <div className="flex flex-col gap-5 px-4 pb-8">
          <Combobox
            className="mt-6"
            label="Select Products (Multi select)"
            options={options}
            maxAllowed={6 + defaultSelected.length}
            defaultValue={defaultSelected}
            lockDefaultValue
            multiple
            onChange={handleSelectProducts}
          />
        </div>

        <Bottombar className="flex gap-4 bg-white">
          <Button variant="outline" onClick={close}>
            Back
          </Button>
          <Button
            variant="default"
            className="flex-1"
            isLoading={isLoading}
            onClick={handleAddProducts}
          >
            Add Products
          </Button>
        </Bottombar>
      </ShadSheetContent>
    </ShadSheet>
  )
}
